import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ShieldCheck } from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";

const navLinks = [
  { to: "/features", label: "Features" },
  { to: "/security", label: "Security" },
  { to: "/pricing", label: "Pricing" },
  { to: "/download", label: "Download" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-2" : "py-3"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div
          className={`glass-light rounded-full px-4 sm:px-5 py-2.5 flex items-center justify-between transition-all duration-300 ${
            scrolled ? "shadow-lg" : ""
          }`}
        >
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="navbar-logo-link"
          >
            <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-mint text-ink shadow-sm">
              <ShieldCheck className="h-5 w-5" strokeWidth={2.4} />
              <span className="absolute -inset-1 rounded-2xl bg-mint/40 blur-md -z-10 group-hover:bg-mint/60 transition" />
            </span>
            <span className="font-heading font-bold tracking-tight text-lg text-foreground">
              AVault
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="navbar-links">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                    isActive
                      ? "bg-foreground/5 text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-foreground/5 transition"
              aria-label="Toggle theme"
              data-testid="theme-toggle-btn"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            <Link to="/download" className="hidden sm:block">
              <Button
                className="rounded-full bg-mint hover:bg-mint-300 text-ink shadow-md"
                data-testid="navbar-cta-download"
              >
                Get AVault
              </Button>
            </Link>
            <button
              className="lg:hidden h-9 w-9 rounded-full inline-flex items-center justify-center hover:bg-foreground/5 transition"
              onClick={() => setOpen((v) => !v)}
              aria-label="Open menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="lg:hidden glass-light rounded-3xl mt-3 p-4 animate-rise"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                      isActive
                        ? "bg-foreground/5 text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
                    }`
                  }
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/download" className="mt-2">
                <Button
                  className="w-full rounded-xl bg-mint hover:bg-mint-300 text-ink"
                  data-testid="mobile-cta-download"
                >
                  Get AVault
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
